/**
 * Ticket Z4: Modul „100 Tage im Amt" – Phasen, Zeitachse und Positionsmarke.
 *
 * Bewusst ohne Serverbezug: reine Berechnung, damit sie testbar bleibt und
 * dieselben Beschriftungen serverseitig wie in der Ansicht gelten.
 *
 * Wichtig: Diese Datei erhält Wahltag und Amtsantritt nur als Eingabe der
 * serverseitigen Aufbereitung. An das Kundenkonto gehen ausschließlich die
 * abgeleiteten Werte aus `ModuleTimeline` – niemals Rohfelder der Kundentabelle.
 */

export type PhaseKey = "zwischenphase" | "tag_1_30" | "tag_31_60" | "tag_61_100";

export const PHASE_KEYS: PhaseKey[] = [
  "zwischenphase",
  "tag_1_30",
  "tag_31_60",
  "tag_61_100",
];

/** Grundbeschriftung ohne Datum. */
export const PHASE_TITLES: Record<PhaseKey, string> = {
  zwischenphase: "1. Zwischenphase",
  tag_1_30: "2. Tag 1 bis 30",
  tag_31_60: "3. Tag 31 bis 60",
  tag_61_100: "4. Tag 61 bis 100",
};

/** Kurzbeschriftung für die Zeitachse (Orientierung, ohne Zeitraum). */
export const PHASE_SHORT_LABELS: Record<PhaseKey, string> = {
  zwischenphase: "1. Zwischenphase",
  tag_1_30: "2. Tag 1–30",
  tag_31_60: "3. Tag 31–60",
  tag_61_100: "4. Tag 61–100",
};

/** Relative Beschriftung, wenn kein Amtsantritt gepflegt ist. */
export const PHASE_RELATIVE_LABELS: Record<PhaseKey, string> = {
  zwischenphase: "1. Zwischenphase bis zum Amtsantritt",
  tag_1_30: "2. Tag 1 bis 30 nach Amtsantritt",
  tag_31_60: "3. Tag 31 bis 60 nach Amtsantritt",
  tag_61_100: "4. Tag 61 bis 100 nach Amtsantritt",
};

/**
 * VORLÄUFIGE Kurzbeschreibungen (Ticket Z4).
 *
 * Der endgültige Wortlaut stammt aus dem Manuskript „100 Tage im Amt" und wird
 * mit Ticket Z7 eingespielt. Bis dahin stehen hier bewusst knappe, neutrale
 * Zwischentexte. Der Austausch ist reine Textpflege an dieser einen Stelle –
 * bitte nur die Werte ersetzen, nicht die Struktur.
 */
export const PHASE_DESCRIPTIONS_PROVISIONAL: Record<PhaseKey, string> = {
  zwischenphase:
    "Vorbereitung zwischen Wahltag und Amtsantritt: Übergabe, Terminplanung und erste Abstimmungen.",
  tag_1_30: "Der Amtsstart: Ankommen, Zuhören und die ersten Signale nach innen und außen.",
  tag_31_60: "Schwerpunkte setzen: Vorhaben ordnen und die Zusammenarbeit festigen.",
  tag_61_100: "Bilanz und Ausblick: Ergebnisse sichtbar machen und den weiteren Kurs abstecken.",
};

/** Tage nach Amtsantritt: [Beginn, Ende] je Phase (Zwischenphase liegt davor). */
const PHASE_DAY_RANGES: Record<Exclude<PhaseKey, "zwischenphase">, [number, number]> = {
  tag_1_30: [1, 30],
  tag_31_60: [31, 60],
  tag_61_100: [61, 100],
};

/**
 * Standardversatz zwischen Wahltag und Amtsantritt in Tagen.
 *
 * Fachlicher Hintergrund: Zwischen Wahltag und Amtsantritt liegen je nach
 * Kommune und Landesrecht mehrere Wochen. 60 Tage sind eine Annäherung an
 * diesen Zeitraum. Sie wird gegenstandslos, sobald das Amtsantrittsdatum am
 * Kunden gepflegt ist.
 *
 * Verwendung ausschließlich für die Position der Marke („rechnerisch"),
 * niemals für datierte Phasenbeschriftungen.
 */
export const DEFAULT_TERM_START_OFFSET_DAYS = 60;

export type TimelineMode = "datiert" | "rechnerisch" | "ohne_daten";

export type TimelinePhase = {
  key: PhaseKey;
  label: string;
  /** Kurzform für die Zeitachse (Ticket Z4a: keine Doppelung der Zeiträume). */
  shortLabel: string;
  dated: boolean;
  startLabel: string | null;
  endLabel: string | null;
  description: string;
  /**
   * Ticket Z5: Zählwerte der Phase (erledigt von gesamt) oder `null`, solange
   * der Aufgabenplan nicht mitgeladen wurde. Enthält niemals Notizinhalte.
   */
  progress: { done: number; total: number } | null;
};

export type ModuleTimeline = {
  mode: TimelineMode;
  /** Sichtbarer Zusatz an der Marke, z. B. „rechnerisch". */
  markerNote: string | null;
  /** Erläuterung des Zusatzes direkt an der Marke, sonst `null`. */
  markerNoteExplanation: string | null;
  /** Position der Marke auf der Achse in Prozent (0–100) oder `null`. */
  markerPercent: number | null;
  markerPhaseKey: PhaseKey | null;
  /** Steuert den dezenten Hinweis, den Amtsantritt nachtragen zu lassen. */
  hintMissingTermStart: boolean;
  /**
   * Ticket Z4a: Wortlaut des Nachtrag-Hinweises. Wird serverseitig gesetzt,
   * damit die Ansicht keine Modus-Prüfung vornehmen muss.
   */
  termStartHint: string | null;
  phases: TimelinePhase[];
};

const DAY_MS = 86_400_000;

const deDate = new Intl.DateTimeFormat("de-DE", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});

function parseDay(value: string | null | undefined): Date | null {
  if (!value) return null;
  const d = new Date(`${String(value).slice(0, 10)}T00:00:00Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}

function addDays(base: Date, days: number): Date {
  return new Date(base.getTime() + days * DAY_MS);
}

function fmt(d: Date): string {
  return deDate.format(d);
}

function clamp(v: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, v));
}

/**
 * Baut die Zeitachse. Ausgabe enthält nur Anzeigewerte.
 *
 * Achsenlogik: Die Achse umfasst die vier Phasen zu gleichen Anteilen
 * (je 25 %). Die Marke wird innerhalb ihrer Phase anteilig gesetzt.
 */
export function buildTimeline(input: {
  electionDay?: string | null;
  termStart?: string | null;
  today?: Date;
}): ModuleTimeline {
  const electionDay = parseDay(input.electionDay);
  const termStart = parseDay(input.termStart);
  const today = input.today ?? new Date();

  const dated = Boolean(termStart);
  const mode: TimelineMode = dated ? "datiert" : electionDay ? "rechnerisch" : "ohne_daten";

  // Nur bei gepflegtem Amtsantritt datieren. Der Standardversatz dient
  // ausschließlich der Marke und niemals einer Beschriftung.
  const phases: TimelinePhase[] = PHASE_KEYS.map((key) => {
    if (!dated || !termStart) {
      return {
        key,
        label: PHASE_RELATIVE_LABELS[key],
        shortLabel: PHASE_SHORT_LABELS[key],
        dated: false,
        startLabel: null,
        endLabel: null,
        description: PHASE_DESCRIPTIONS_PROVISIONAL[key],
        progress: null,
      };
    }
    if (key === "zwischenphase") {
      return {
        key,
        label: PHASE_TITLES[key],
        shortLabel: PHASE_SHORT_LABELS[key],
        dated: true,
        startLabel: electionDay ? fmt(electionDay) : "Wahltag",
        endLabel: fmt(addDays(termStart, -1)),
        description: PHASE_DESCRIPTIONS_PROVISIONAL[key],
        progress: null,
      };
    }
    const [from, to] = PHASE_DAY_RANGES[key];
    return {
      key,
      label: PHASE_TITLES[key],
      shortLabel: PHASE_SHORT_LABELS[key],
      dated: true,
      startLabel: fmt(addDays(termStart, from - 1)),
      endLabel: fmt(addDays(termStart, to - 1)),
      description: PHASE_DESCRIPTIONS_PROVISIONAL[key],
      progress: null,
    };
  });

  // Referenz-Amtsantritt für die Marke: echt oder rechnerisch.
  const markerTermStart =
    termStart ?? (electionDay ? addDays(electionDay, DEFAULT_TERM_START_OFFSET_DAYS) : null);

  let markerPercent: number | null = null;
  let markerPhaseKey: PhaseKey | null = null;

  if (markerTermStart) {
    const dayIndex = Math.floor((today.getTime() - markerTermStart.getTime()) / DAY_MS) + 1;
    if (dayIndex < 1) {
      markerPhaseKey = "zwischenphase";
      // Innerhalb der Zwischenphase anteilig, sofern ein Wahltag bekannt ist.
      const spanStart = electionDay ?? addDays(markerTermStart, -DEFAULT_TERM_START_OFFSET_DAYS);
      const span = Math.max(1, (markerTermStart.getTime() - spanStart.getTime()) / DAY_MS);
      const done = clamp((today.getTime() - spanStart.getTime()) / DAY_MS / span, 0, 1);
      markerPercent = Math.round(done * 25 * 10) / 10;
    } else {
      const idx = dayIndex <= 30 ? 1 : dayIndex <= 60 ? 2 : 3;
      const key = (["tag_1_30", "tag_31_60", "tag_61_100"] as PhaseKey[])[idx - 1]!;
      markerPhaseKey = key;
      const [from, to] = PHASE_DAY_RANGES[key as Exclude<PhaseKey, "zwischenphase">];
      const within = clamp((dayIndex - from) / (to - from + 1), 0, 1);
      markerPercent = Math.round((idx * 25 + within * 25) * 10) / 10;
      if (dayIndex > 100) {
        markerPhaseKey = "tag_61_100";
        markerPercent = 100;
      }
    }
    markerPercent = clamp(markerPercent, 0, 100);
  }

  const termStartHint =
    mode === "rechnerisch"
      ? "Ihre Zeitachse ist derzeit geschätzt. Tragen Sie Ihren Amtsantritt ein, dann rechnen wir taggenau. Bei Fragen unterstützt Sie Ihr Beratungsteam."
      : mode === "ohne_daten"
        ? "Für eine genaue Zeitachse fehlt uns noch Ihr Amtsantritt. Tragen Sie das Datum hier selbst ein – Ihr Beratungsteam hilft bei Fragen gern weiter."
        : null;

  return {
    mode,
    markerNote: mode === "rechnerisch" ? "rechnerisch" : null,
    markerNoteExplanation: mode === "rechnerisch" ? "geschätzt anhand Ihres Wahltags" : null,
    markerPercent,
    markerPhaseKey,
    hintMissingTermStart: !dated,
    termStartHint,
    phases,
  };
}

/** Beschriftung der Phase für Kachel und Achse. */
export function phaseRangeLabel(phase: TimelinePhase): string | null {
  if (!phase.dated || !phase.startLabel || !phase.endLabel) return null;
  return `${phase.startLabel} – ${phase.endLabel}`;
}

export function isPhaseKey(value: string): value is PhaseKey {
  return (PHASE_KEYS as string[]).includes(value);
}

/**
 * Ausrichtung der Positionsmarke „Sie sind hier" über der waagerechten Achse.
 *
 * Ohne Einfangen ragt die Pille an den Enden aus dem Kasten und wird
 * abgeschnitten. Darum wird der Verschiebungsanteil von der Position
 * abhängig gemacht: am Anfang bündig links (0 %), im Mittelfeld zentriert
 * (50 %), am Ende bündig rechts (100 %). Der Zeiger bleibt immer exakt auf
 * dem berechneten Prozentwert.
 */
export function markerAlignment(percent: number): {
  /** Position der Pille in Prozent der Achsenbreite. */
  left: number;
  /** Anteil der eigenen Breite, um den die Pille nach links rückt (0–100). */
  shiftPercent: number;
} {
  const left = Math.min(100, Math.max(0, percent));
  const LOW = 15;
  const HIGH = 85;
  let shiftPercent: number;
  if (left <= LOW) shiftPercent = 0;
  else if (left >= HIGH) shiftPercent = 100;
  else shiftPercent = ((left - LOW) / (HIGH - LOW)) * 100;
  return { left, shiftPercent: Math.round(shiftPercent * 10) / 10 };
}
